import { routerReducer, RouterReducerState } from '@ngrx/router-store';
import { ActionReducerMap } from '@ngrx/store';
import { AdminState } from 'app/core/store/admin/admin.interface';
import { CalendarState } from 'app/core/store/calendar/common/calendar.interface';
import { ContractInfo } from 'app/core/store/contract-info/contract-info.interface';
import { CustomerInfo } from 'app/core/store/customer-info/customer-info.interface';
import { SessionState } from 'app/core/store/session/session.interface';
import { UI } from 'app/core/store/ui/ui.interface';
import { SapInvoicesState } from '../interfaces/sap-invoice.interface';
import { I18n } from './admin/i18n/i18n.interface';
import { BlankReceipt } from './blank-receipts/blank-receipts.interface';
import { Config } from './config/config.interface';
import { ContractDetails } from './contract-details/contract-details.interface';
import { Definitions } from './definitions/definitions.interface';
import { EmailActors } from './email-actors/email-actors.interface';
import { Florders } from '../interfaces/florders.interface';
import { EditFlowDetail } from './edit-flow-detail/flow-detail.interface';
import { EditOrderDetail } from './order-detail/order-detail.interface';
import { EditRelocationDetail } from './edit-relocation-detail/relocation-detail.interface';
import { Service } from './service/service.interface';
import { Stock } from './stock/stock.interface';
import { TemplateState } from './template/template.interface';

// reducers
import { reducer as adminReducer } from './admin/admin.reducer';
import { reducer as i18nreducer } from './admin/i18n/i18n.reducer';
import { reducer as blankReceiptsReducer } from './blank-receipts/blank-receipts.reducer';
import { reducer as editCCRDetailReducer } from './edit-ccr-detail/ccr-detail.reducer';
import { reducer as calendarReducer } from './calendar/calendar.reducer';
import { reducer as configReducer } from './config/config.reducer';
import { reducer as contractDetailsReducer } from './contract-details/contract-details.reducer';
import { reducer as contractInfoReducer } from './contract-info/contract-info.reducer';
import { reducer as customerInfoReducer } from './customer-info/customer-info.reducer';
import { reducer as definitionsReducer } from './definitions/definitions.reducer';
import { reducer as emailActorsReducer } from './email-actors/email-actors.reducer';
import { reducer as editFlowDetailReducer } from './edit-flow-detail/flow-detail.reducer';
import { reducer as flowsReducer } from './flows/flows.reducer';
import { reducer as eInvoiceReducer } from './e-invoice/e-invoice.reducer';
import { reducer as sapInvoiceReducer } from './sap-invoice/sap-invoice.reducer';
import { reducer as editOrderDetailReducer } from './order-detail/order-detail.reducer';
import { reducer as ordersReducer } from './orders/orders.reducer';
import { reducer as editRelocationDetailReducer } from './edit-relocation-detail/relocation-detail.reducer';
import { reducer as relocationsReducer } from './relocations/relocations.reducer';
import { reducer as serviceReducer } from './service/service.reducer';
import { reducer as sessionReducer } from './session/session.reducer';
import { reducer as stockReducer } from './stock/stock.reducer';
import { reducer as templateReducer } from './template/template.reducer';
import { reducer as fromUiReducer } from './ui/ui.reducer';
import { reducer as stockCountReducer } from './stock-count/stock-count.reducer';
import { StockCountState } from './stock-count/stock-count.interface';
import { Orders } from '../interfaces/order.interface';
import { EInvoicesState } from '../interfaces/e-invoice.interface';

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface RootState {
  admin: AdminState;
  blankReceipts: BlankReceipt;
  calendar: CalendarState;
  config: Config;
  contractDetails: ContractDetails;
  contractInfo: ContractInfo;
  customerInfo: CustomerInfo;
  definitions: Definitions;
  editCCRDetail: EditOrderDetail;
  editFlowDetail: EditFlowDetail;
  editOrderDetail: EditOrderDetail;
  editRelocationDetail: EditRelocationDetail;
  emailActors: EmailActors;
  flows: Florders;
  i18n: I18n;
  sapInvoice: SapInvoicesState;
  eInvoice: EInvoicesState;
  orders: Orders;
  relocations: Florders;
  router: RouterReducerState;
  service: Service;
  session: SessionState;
  stock: Stock;
  stockCount: StockCountState;
  template: TemplateState;
  ui: UI;
}

/**
 * Because metareducers take a reducer function and return a new reducer,
 * we can use our compose helper to chain them together. Here we are
 * using combineReducers to make our top level reducer, and then
 * wrapping that in storeLogger. Remember that compose applies
 * the result from right to left.
 */
export const reducers: ActionReducerMap<RootState> = {
  admin: adminReducer,
  blankReceipts: blankReceiptsReducer,
  calendar: calendarReducer,
  config: configReducer,
  contractDetails: contractDetailsReducer,
  contractInfo: contractInfoReducer,
  customerInfo: customerInfoReducer,
  definitions: definitionsReducer,
  editCCRDetail: editCCRDetailReducer,
  editFlowDetail: editFlowDetailReducer,
  editOrderDetail: editOrderDetailReducer,
  editRelocationDetail: editRelocationDetailReducer,
  emailActors: emailActorsReducer,
  flows: flowsReducer,
  i18n: i18nreducer,
  sapInvoice: sapInvoiceReducer,
  eInvoice: eInvoiceReducer,
  orders: ordersReducer,
  relocations: relocationsReducer,
  router: routerReducer,
  service: serviceReducer,
  session: sessionReducer,
  stock: stockReducer,
  stockCount: stockCountReducer,
  template: templateReducer,
  ui: fromUiReducer,
};
